import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import BasePage from '../../components/Layout/BasePage';
import ProductionTreeView from './ProductionTreeView';
import SupervisorCostTracker from './SupervisorCostTracker';
import GoodsReceiptManager from './GoodsReceiptManager';
// Removed AllocationManager - moved to WarehouseHomePage
import OrderTracer from './OrderTracer';
import RequisitionApprovals from './RequisitionApprovals';
import SupervisorOverview from './SupervisorOverview';
import WasteReviewDashboard from '../../components/Tasks/WasteReviewDashboard';
import './SupervisorHomePage.css';

const SupervisorHomePage = () => {
  const { t } = useTranslation('supervisor');
  const { user } = useAuth();

  // Initialize state with saved values or defaults
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('supervisorActiveTab') || 'overview';
  });
  const [activeSubTab, setActiveSubTab] = useState(() => {
    return sessionStorage.getItem('supervisorActiveSubTab') || 'dashboard';
  });
  // Record to open in the target subtab when navigating from a dashboard card (e.g. { requisitionId })
  const [subTabOptions, setSubTabOptions] = useState(null);

  // Refs for component refresh methods
  const overviewRef = useRef();
  const productionTreeRef = useRef();
  const costTrackerRef = useRef();
  const goodsReceiptRef = useRef();
  const wasteReviewRef = useRef();
  const orderTracerRef = useRef();
  const requisitionApprovalsRef = useRef();

  // Save tab state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('supervisorActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    sessionStorage.setItem('supervisorActiveSubTab', activeSubTab);
  }, [activeSubTab]);

  // Tab configuration using WarehouseDashboard pattern
  const tabConfig = {
    'overview': {
      name: t('navigation.tabs.dashboard'),
      icon: '📊',
      subtabs: {
        'dashboard': { name: t('navigation.subtabs.overview'), icon: '📋' }
      }
    },
    'production': {
      name: t('navigation.tabs.production'),
      icon: '🏭',
      subtabs: {
        'goods-receipt': { name: t('navigation.subtabs.goodsReceipt'), icon: '📥' },
        'cost-management': { name: t('navigation.subtabs.costManagement'), icon: '💰' },
        'waste-review': { name: 'Waste Review', icon: '🔍' }
      }
    },
    'inventory': {
      name: t('navigation.tabs.inventory'),
      icon: '📦',
      subtabs: {
        'requisition-approvals': { name: t('navigation.subtabs.materialApprovals'), icon: '✅' }
      }
    },
    'tools': {
      name: t('navigation.tabs.tools'),
      icon: '🔧',
      subtabs: {
        'order-tracer': { name: t('navigation.subtabs.orderTracer'), icon: '🔍' },
        'production-orders': { name: t('navigation.subtabs.orderOverview'), icon: '📦' }
      }
    }
  };

  // Enhanced navigation function for overview clicks
  const handleNavigateToTab = (tabId, options = null) => {
    setSubTabOptions(options);
    // Check if it's a main tab ID directly
    if (tabConfig[tabId]) {
      setActiveTab(tabId);
      const firstSubtab = Object.keys(tabConfig[tabId].subtabs)[0];
      if (firstSubtab) setActiveSubTab(firstSubtab);
      return;
    }
    // Find which main tab contains this subtab
    for (const [mainTabId, tab] of Object.entries(tabConfig)) {
      if (Object.keys(tab.subtabs).includes(tabId)) {
        setActiveTab(mainTabId);
        setActiveSubTab(tabId);
        return;
      }
    }
    // Fallback to old behavior
    setActiveSubTab(tabId);
  };

  // Handle refresh for all components - only refresh data, not full page
  const handleRefresh = () => {
    // Always try to call component refresh method first
    // If component doesn't have refresh method, it will just silently do nothing (no page reload)
    switch (activeSubTab) {
      case 'dashboard':
        if (overviewRef.current?.refresh) {
          overviewRef.current.refresh();
        }
        break;
      case 'production-orders':
        if (productionTreeRef.current?.refresh) {
          productionTreeRef.current.refresh();
        }
        break;
      case 'cost-management':
        if (costTrackerRef.current?.refresh) {
          costTrackerRef.current.refresh();
        }
        break;
      case 'goods-receipt':
        if (goodsReceiptRef.current?.refresh) {
          goodsReceiptRef.current.refresh();
        }
        break;
      case 'waste-review':
        if (wasteReviewRef.current?.refresh) {
          wasteReviewRef.current.refresh();
        }
        break;
      case 'order-tracer':
        if (orderTracerRef.current?.refresh) {
          orderTracerRef.current.refresh();
        }
        break;
      case 'requisition-approvals':
        if (requisitionApprovalsRef.current?.refresh) {
          requisitionApprovalsRef.current.refresh();
        }
        break;
      default:
        // No fallback to page reload - just silently do nothing if no refresh method
        break;
    }
  };

  const renderTabContent = () => {
    switch (activeSubTab) {
      case 'dashboard':
        return <SupervisorOverview ref={overviewRef} onNavigateToTab={handleNavigateToTab} />;

      case 'production-orders':
        return <ProductionTreeView ref={productionTreeRef} />;

      case 'cost-management':
        return <SupervisorCostTracker ref={costTrackerRef} />;

      case 'goods-receipt':
        return (
          <GoodsReceiptManager
            key={subTabOptions?.receiptId || 'goods-receipt'}
            ref={goodsReceiptRef}
            initialTab={subTabOptions?.receiptTab}
            highlightReceiptId={subTabOptions?.receiptId}
          />
        );

      case 'waste-review':
        return <WasteReviewDashboard ref={wasteReviewRef} />;

      // Allocation moved to Warehouse view

      case 'order-tracer':
        return <OrderTracer ref={orderTracerRef} />;

      case 'requisition-approvals':
        return <RequisitionApprovals ref={requisitionApprovalsRef} initialRequisitionId={subTabOptions?.requisitionId} />;

      default:
        return null;
    }
  };

  return (
    <BasePage title={t('navigation.title')} className="supervisor-home-page">
      <div className="supervisor-navigation">
        {/* Main Tabs */}
        <div className="main-tabs">
          {Object.entries(tabConfig).map(([tabId, tab]) => (
            <button
              key={tabId}
              className={`main-tab ${activeTab === tabId ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tabId);
                // Set first subtab as active when switching main tabs
                const firstSubTabId = Object.keys(tab.subtabs)[0];
                setActiveSubTab(firstSubTabId);
                setSubTabOptions(null);
              }}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Sub Tabs */}
        <div className="sub-tabs">
          <div className="subtabs-left">
            {tabConfig[activeTab] && Object.entries(tabConfig[activeTab].subtabs).map(([subTabId, subTab]) => (
              <button
                key={subTabId}
                className={`sub-tab ${activeSubTab === subTabId ? 'active' : ''}`}
                onClick={() => { setActiveSubTab(subTabId); setSubTabOptions(null); }}
              >
                <span className="subtab-icon">{subTab.icon}</span>
                <span className="subtab-label">{subTab.name}</span>
              </button>
            ))}
          </div>
          <button
            className="btn-refresh-subtab"
            onClick={handleRefresh}
          >
            🔄 {t('buttons.refresh')}
          </button>
        </div>
      </div>

      <div className="supervisor-content">
        {renderTabContent()}
      </div>
    </BasePage>
  );
};

export default SupervisorHomePage;
